import { AutocompleteChips } from '@talentmesh/ui/components/autocomplete-chips';
import type { AutocompleteOption } from '@talentmesh/ui/components/autocomplete-chips';
import { Button } from '@talentmesh/ui/components/button';
import { Checkbox } from '@talentmesh/ui/components/checkbox';
import { Slider } from '@talentmesh/ui/components/slider';
import { FormProvider, useWatch } from 'react-hook-form';
import useSearchAutocompleteOptions, {
    SearchAutocompleteRequest,
    SearchAutocompleteType,
} from '../../../../api/useSearchAutocompleteOptions';
import {
    LanguageProficiency,
    LanguageProficiencyLevelList,
    SearchPeriod,
    SearchPeriodList,
} from '../../../../api/useSearchFilters';
import { maxYearsOfExperience, minYearsOfExperience } from '../../utils/IdentifiedListContext';
import { COMPANY_FILTER_LIMIT, FILTERS_LIMIT } from '../FiltersLimitConsts';
import { useRefineSearchFiltersFormContext } from '../useRefineSearchFiltersForm';
import ClearAllButton from './ClearAllButton';
import { FilterMenuButton, FilterMenuButtonOption } from './FilterMenuButton';
import FiltersField from './FiltersField';
import Label from './Label';
import { LocationAutocompleteField } from './LocationAutocomplete';
import supportedLanguages from './supportedSpeakingLanguages';
import useJobTitleSynonymsAdder from './useJobTitleSynonymsAdder';
import UIStrings from '@/Utils/UIStrings';

export function searchPeriodToString(searchPeriod: SearchPeriod): string {
    switch (searchPeriod) {
        case 'Current':
            return UIStrings.CurrentExperience;
        case 'Last3Years':
            return UIStrings.CurrentAnd3YearsBack;
        case 'Last5Years':
            return UIStrings.CurrentAnd5YearsBack;
        case 'AllTime':
            return UIStrings.AllExperiences;
        default:
            return '';
    }
}

const experienceFilterOptions: FilterMenuButtonOption<SearchPeriod>[] = SearchPeriodList.map((item) => {
    return { value: item, label: searchPeriodToString(item) };
});

function languageProficiencyLevelToString(level: LanguageProficiency): string {
    switch (level) {
        case 'AnyProficiency':
            return UIStrings.AnyProficiencyLevel;
        case 'ElementaryProficiency':
            return UIStrings.ElementaryProficiency;
        case 'LimitedWorkingProficiency':
            return UIStrings.LimitedWorkingProficiency;
        case 'ProfessionalWorkingProficiency':
            return UIStrings.ProfessionalWorkingProficiency;
        case 'FullProfessionalProficiency':
            return UIStrings.FullProfessionalProficiency;
        case 'NativeOrBilingualProficiency':
            return UIStrings.NativeOrBilingualProficiency;
        default:
            return '';
    }
}

const proficiencyFilterOptions: FilterMenuButtonOption<LanguageProficiency>[] = LanguageProficiencyLevelList.map(
    (item) => {
        return { value: item, label: languageProficiencyLevelToString(item) };
    },
);

const FiltersForm = () => {
    const form = useRefineSearchFiltersFormContext();
    const { addSynonyms, hasJobTitles, isLimitHit } = useJobTitleSynonymsAdder();

    const yearsOfExperience = useWatch({ control: form.control, name: 'yearsOfExperience' });
    const includeEmptyExperience = useWatch({ control: form.control, name: 'includeEmptyExperience' });
    const jobTitleSearchPeriod = useWatch({ control: form.control, name: 'jobTitleSearchPeriod' });
    const companiesSearchPeriod = useWatch({ control: form.control, name: 'companiesSearchPeriod' });
    const languageProficiency = useWatch({ control: form.control, name: 'languageProficiency' });

    const getEmptyAutocompleteOptions = async () => [] as AutocompleteOption[];
    const getLanguageAutocompleteOptions = async (search: string): Promise<AutocompleteOption[]> => {
        const query = search.toLowerCase();
        return supportedLanguages.filter((lang) => lang.label.toLowerCase().startsWith(query));
    };
    const formatMaxYearsOfExperience = (value: number) => (value === maxYearsOfExperience ? `${value}+` : value);

    const { trigger } = useSearchAutocompleteOptions();

    const getSearchAutocompleteOptions = async (
        search: string,
        type: SearchAutocompleteType,
    ): Promise<AutocompleteOption[]> => {
        const payload: SearchAutocompleteRequest = {
            query: search,
            type,
        };
        const response = await trigger(payload);

        const options = response.options.map((option: string) => ({
            id: option,
            label: option,
        }));

        return options;
    };

    const getJobTitleSearchAutocompleteOptions = async (search: string): Promise<AutocompleteOption[]> => {
        return await getSearchAutocompleteOptions(search, SearchAutocompleteType.JobTitle);
    };

    const getCompanyNameSearchAutocompleteOptions = async (search: string): Promise<AutocompleteOption[]> => {
        return await getSearchAutocompleteOptions(search, SearchAutocompleteType.CompanyName);
    };

    return (
        <FormProvider {...form}>
            <div className="flex w-full flex-col gap-2.5">
                <FiltersField>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <span className="text-foreground text-sm font-medium">{UIStrings.JobTitles}</span>
                            <FilterMenuButton
                                options={experienceFilterOptions}
                                selectedValue={jobTitleSearchPeriod}
                                onSelect={(value) =>
                                    form.setValue('jobTitleSearchPeriod', value, { shouldDirty: true })
                                }
                                disabled={form.formState.disabled}
                            />
                        </div>
                        <ClearAllButton onClearAll={() => form.setValue('jobTitles', [], { shouldDirty: true })} />
                    </div>
                    <div data-cy="jobTitles.label">
                        <AutocompleteChips
                            getAutocompleteOptions={getJobTitleSearchAutocompleteOptions}
                            name="jobTitles"
                            placeholder={UIStrings.AddJobTitle}
                            limit={FILTERS_LIMIT}
                            disabled={form.formState.disabled}
                        />
                    </div>
                    {hasJobTitles && (
                        <Button
                            role="button"
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto self-start p-0"
                            onClick={addSynonyms}
                            disabled={isLimitHit}
                        >
                            {UIStrings.AddSimilarJobTitles}
                        </Button>
                    )}
                </FiltersField>
                <FiltersField>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <span className="text-foreground text-sm font-medium">{UIStrings.Companies}</span>
                            <FilterMenuButton
                                options={experienceFilterOptions}
                                selectedValue={companiesSearchPeriod}
                                onSelect={(value) =>
                                    form.setValue('companiesSearchPeriod', value, { shouldDirty: true })
                                }
                                disabled={form.formState.disabled}
                            />
                        </div>
                        <ClearAllButton onClearAll={() => form.setValue('companies', [], { shouldDirty: true })} />
                    </div>
                    <div data-cy="companies.label">
                        <AutocompleteChips
                            getAutocompleteOptions={getCompanyNameSearchAutocompleteOptions}
                            name="companies"
                            placeholder={UIStrings.AddCompany}
                            limit={COMPANY_FILTER_LIMIT}
                            disabled={form.formState.disabled}
                        />
                    </div>
                </FiltersField>
                <LocationAutocompleteField />
                <FiltersField>
                    <Label
                        title={UIStrings.Keywords}
                        onClearAll={() => form.setValue('keywords', [], { shouldDirty: true })}
                    />
                    <AutocompleteChips
                        getAutocompleteOptions={getEmptyAutocompleteOptions}
                        name="keywords"
                        placeholder={UIStrings.AddKeyword}
                        limit={FILTERS_LIMIT}
                        disabled={form.formState.disabled}
                    />
                </FiltersField>
                <FiltersField>
                    <Label
                        title={UIStrings.YearsOfExperience}
                        onClearAll={() =>
                            form.setValue('yearsOfExperience', [minYearsOfExperience, maxYearsOfExperience], {
                                shouldDirty: true,
                            })
                        }
                    />
                    <div className="max-w-[265px] px-2">
                        <Slider
                            className="w-full"
                            min={minYearsOfExperience}
                            max={maxYearsOfExperience}
                            step={1}
                            value={yearsOfExperience}
                            onValueChange={(value) =>
                                form.setValue('yearsOfExperience', value as [number, number], { shouldDirty: true })
                            }
                            disabled={form.formState.disabled}
                            data-cy="yearsOfExperienceSlider"
                        />
                        <div className="mt-2 flex justify-between">
                            <span className="text-muted-foreground text-xs">{yearsOfExperience?.[0]}</span>
                            <span className="text-muted-foreground text-xs">
                                {formatMaxYearsOfExperience(yearsOfExperience?.[1])}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="includeEmptyExperience"
                            name="includeEmptyExperience"
                            checked={includeEmptyExperience}
                            onCheckedChange={(checked) =>
                                form.setValue('includeEmptyExperience', checked === true, { shouldDirty: true })
                            }
                            disabled={form.formState.disabled}
                        />
                        <label
                            htmlFor="includeEmptyExperience"
                            className={
                                form.formState.disabled
                                    ? 'cursor-not-allowed text-sm opacity-70'
                                    : 'cursor-pointer text-sm'
                            }
                        >
                            {UIStrings.IncludeEmptyExperience}
                        </label>
                    </div>
                </FiltersField>
                <FiltersField>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <span className="text-foreground text-sm font-medium">{UIStrings.Languages}</span>
                            <FilterMenuButton
                                options={proficiencyFilterOptions}
                                selectedValue={languageProficiency}
                                onSelect={(value) => form.setValue('languageProficiency', value, { shouldDirty: true })}
                                disabled={form.formState.disabled}
                            />
                        </div>
                        <ClearAllButton onClearAll={() => form.setValue('languages', [], { shouldDirty: true })} />
                    </div>
                    <div data-cy="languages.label">
                        <AutocompleteChips
                            getAutocompleteOptions={getLanguageAutocompleteOptions}
                            name="languages"
                            placeholder={UIStrings.AddLanguage}
                            limit={FILTERS_LIMIT}
                            disabled={form.formState.disabled}
                            disableFreeSolo
                        />
                    </div>
                </FiltersField>
            </div>
        </FormProvider>
    );
};

export default FiltersForm;
